import React from 'react';
import { Icon } from '@iconify/react';
import { Segment } from '@heroui-pro/react';
import { useTranslation } from 'react-i18next';

type ThemeMode = 'light' | 'dark' | 'system';
type ThemeStyle = 'glass' | 'brutalism';

type DashboardThemeToggleProps = {
    className?: string;
    id: string;
    itemIdPrefix: string;
};

const themeModeStorageKey = 'bos-theme-mode';
const themeStyleStorageKey = 'bos-theme-style';
const themeModeChangeEvent = 'bos-theme-mode-change';
const themeStyleChangeEvent = 'bos-theme-style-change';

const themeModes = [
    'light',
    'dark',
    'system',
] as const satisfies readonly ThemeMode[];
const themeStyles = [
    'glass',
    'brutalism',
] as const satisfies readonly ThemeStyle[];
const themeModeItems = [
    {
        icon: 'solar:sun-2-linear',
        labelKey: 'layout.theme.light',
        mode: 'light',
    },
    {
        icon: 'solar:moon-linear',
        labelKey: 'layout.theme.dark',
        mode: 'dark',
    },
    {
        icon: 'solar:monitor-linear',
        labelKey: 'layout.theme.system',
        mode: 'system',
    },
] as const satisfies readonly {
    icon: string;
    labelKey: string;
    mode: ThemeMode;
}[];
const themeStyleItems = [
    {
        icon: 'eva:radio-button-off-outline',
        labelKey: 'layout.theme.glass',
        style: 'glass',
    },
    {
        icon: 'eva:square-fill',
        labelKey: 'layout.theme.brutalism',
        style: 'brutalism',
    },
] as const satisfies readonly {
    icon: string;
    labelKey: string;
    style: ThemeStyle;
}[];

const isThemeMode = (value: string | null): value is ThemeMode => {
    return themeModes.includes(value as ThemeMode);
};

const isThemeStyle = (value: string | null): value is ThemeStyle => {
    return themeStyles.includes(value as ThemeStyle);
};

const getSystemTheme = (): Exclude<ThemeMode, 'system'> => {
    return window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';
};

const applyDocumentTheme = (mode: ThemeMode, style: ThemeStyle) => {
    const resolvedMode = mode === 'system' ? getSystemTheme() : mode;
    const root = document.documentElement;

    root.dataset.theme = `${style}-${resolvedMode}`;
    root.classList.toggle('dark', resolvedMode === 'dark');
    root.classList.toggle('light', resolvedMode === 'light');
};

const getStoredThemeMode = (): ThemeMode => {
    if (typeof window === 'undefined') {
        return 'light';
    }

    const storedThemeMode = window.localStorage.getItem(themeModeStorageKey);

    return isThemeMode(storedThemeMode) ? storedThemeMode : 'light';
};

const getStoredThemeStyle = (): ThemeStyle => {
    if (typeof window === 'undefined') {
        return 'glass';
    }

    const storedThemeStyle = window.localStorage.getItem(themeStyleStorageKey);

    return isThemeStyle(storedThemeStyle) ? storedThemeStyle : 'glass';
};

/**
 * Applies the stored theme to the document. Called once at startup
 * (`frontend.tsx`): the toggles that otherwise apply it live in the shell, so
 * without this a full-screen page (login, the onboarding wizard,
 * setup-pending) always opened in `index.html`'s light default.
 */
export const applyStoredDocumentTheme = (): void =>
    applyDocumentTheme(getStoredThemeMode(), getStoredThemeStyle());

const useThemeMode = () => {
    const [themeMode, setThemeModeState] =
        React.useState<ThemeMode>(getStoredThemeMode);

    React.useEffect(() => {
        applyDocumentTheme(themeMode, getStoredThemeStyle());
        window.localStorage.setItem(themeModeStorageKey, themeMode);

        if (themeMode !== 'system') {
            return undefined;
        }

        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleSystemThemeChange = () =>
            applyDocumentTheme('system', getStoredThemeStyle());

        mediaQuery.addEventListener('change', handleSystemThemeChange);

        return () => {
            mediaQuery.removeEventListener('change', handleSystemThemeChange);
        };
    }, [themeMode]);

    React.useEffect(() => {
        const handleThemeModeChange = (event: Event) => {
            const nextThemeMode =
                event instanceof CustomEvent && isThemeMode(event.detail?.mode)
                    ? event.detail.mode
                    : getStoredThemeMode();

            setThemeModeState(nextThemeMode);
        };

        const handleStorageChange = (event: StorageEvent) => {
            if (event.key === themeModeStorageKey) {
                setThemeModeState(getStoredThemeMode());
            }
        };

        window.addEventListener(themeModeChangeEvent, handleThemeModeChange);
        window.addEventListener('storage', handleStorageChange);

        return () => {
            window.removeEventListener(
                themeModeChangeEvent,
                handleThemeModeChange,
            );
            window.removeEventListener('storage', handleStorageChange);
        };
    }, []);

    const setThemeMode = React.useCallback((nextThemeMode: ThemeMode) => {
        setThemeModeState(nextThemeMode);
        window.dispatchEvent(
            new CustomEvent(themeModeChangeEvent, {
                detail: {
                    mode: nextThemeMode,
                },
            }),
        );
    }, []);

    return [themeMode, setThemeMode] as const;
};

const useThemeStyle = () => {
    const [themeStyle, setThemeStyleState] =
        React.useState<ThemeStyle>(getStoredThemeStyle);

    React.useEffect(() => {
        applyDocumentTheme(getStoredThemeMode(), themeStyle);
        window.localStorage.setItem(themeStyleStorageKey, themeStyle);
    }, [themeStyle]);

    React.useEffect(() => {
        const handleThemeStyleChange = (event: Event) => {
            const nextThemeStyle =
                event instanceof CustomEvent &&
                isThemeStyle(event.detail?.style)
                    ? event.detail.style
                    : getStoredThemeStyle();

            setThemeStyleState(nextThemeStyle);
        };

        const handleStorageChange = (event: StorageEvent) => {
            if (event.key === themeStyleStorageKey) {
                setThemeStyleState(getStoredThemeStyle());
            }
        };

        window.addEventListener(themeStyleChangeEvent, handleThemeStyleChange);
        window.addEventListener('storage', handleStorageChange);

        return () => {
            window.removeEventListener(
                themeStyleChangeEvent,
                handleThemeStyleChange,
            );
            window.removeEventListener('storage', handleStorageChange);
        };
    }, []);

    const setThemeStyle = React.useCallback((nextThemeStyle: ThemeStyle) => {
        setThemeStyleState(nextThemeStyle);
        window.dispatchEvent(
            new CustomEvent(themeStyleChangeEvent, {
                detail: {
                    style: nextThemeStyle,
                },
            }),
        );
    }, []);

    return [themeStyle, setThemeStyle] as const;
};

export const DashboardStyleToggle: React.FC<DashboardThemeToggleProps> = ({
    className,
    id,
    itemIdPrefix,
}) => {
    const { t } = useTranslation('components');
    const [themeStyle, setThemeStyle] = useThemeStyle();
    const styleToggleRootProps = {
        'data-testid': id,
        id,
    };

    const items = React.useMemo(
        () =>
            themeStyleItems.map((themeStyleItem) => ({
                ...themeStyleItem,
                id: `${itemIdPrefix}-style-${themeStyleItem.style}-button`,
            })),
        [itemIdPrefix],
    );

    return (
        <Segment
            {...styleToggleRootProps}
            className={className}
            onSelectionChange={(key) => {
                const nextThemeStyleItem = items.find(
                    (themeStyleItem) => themeStyleItem.id === String(key),
                );

                if (nextThemeStyleItem) {
                    setThemeStyle(nextThemeStyleItem.style);
                }
            }}
            selectedKey={
                items.find(
                    (themeStyleItem) => themeStyleItem.style === themeStyle,
                )?.id
            }
            size="sm"
        >
            {items.map((themeStyleItem) => (
                <Segment.Item
                    aria-label={t(themeStyleItem.labelKey)}
                    className="size-7 px-0"
                    data-testid={themeStyleItem.id}
                    id={themeStyleItem.id}
                    key={themeStyleItem.id}
                >
                    <Icon
                        className="size-3.5"
                        data-testid={`${themeStyleItem.id}-icon`}
                        icon={themeStyleItem.icon}
                        id={`${themeStyleItem.id}-icon`}
                    />
                </Segment.Item>
            ))}
        </Segment>
    );
};

export const DashboardThemeToggle: React.FC<DashboardThemeToggleProps> = ({
    className,
    id,
    itemIdPrefix,
}) => {
    const { t } = useTranslation('components');
    const [themeMode, setThemeMode] = useThemeMode();
    const themeToggleRootProps = {
        'data-testid': id,
        id,
    };

    const items = React.useMemo(
        () =>
            themeModeItems.map((themeModeItem) => ({
                ...themeModeItem,
                id: `${itemIdPrefix}-theme-${themeModeItem.mode}-button`,
            })),
        [itemIdPrefix],
    );

    return (
        <Segment
            {...themeToggleRootProps}
            className={className}
            onSelectionChange={(key) => {
                const nextThemeModeItem = items.find(
                    (themeModeItem) => themeModeItem.id === String(key),
                );

                if (nextThemeModeItem) {
                    setThemeMode(nextThemeModeItem.mode);
                }
            }}
            selectedKey={
                items.find((themeModeItem) => themeModeItem.mode === themeMode)
                    ?.id
            }
            size="sm"
        >
            {items.map((themeModeItem) => (
                <Segment.Item
                    aria-label={t(themeModeItem.labelKey)}
                    className="size-7 px-0"
                    data-testid={themeModeItem.id}
                    id={themeModeItem.id}
                    key={themeModeItem.id}
                >
                    <Icon
                        className="size-3.5"
                        data-testid={`${themeModeItem.id}-icon`}
                        icon={themeModeItem.icon}
                        id={`${themeModeItem.id}-icon`}
                    />
                </Segment.Item>
            ))}
        </Segment>
    );
};
